import React, { useEffect } from "react";
import { Modal } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { css } from "@emotion/css";
import { useGlobalAppStore } from "@sharpana/shared/stores/globalAppStore";
import { useAuthStatus } from "@sharpana/auth/authHooks";

const modalStyles = {
  content: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 1rem;
    text-align: center;

    @media (min-width: 768px) {
      padding: 2rem;
    }
  `,
  message: css`
    font-size: 1.125rem;
    margin-bottom: 1.5rem;
    line-height: 1.5;
    color: #e0e0e0;
    max-width: 90%;

    @media (min-width: 768px) {
      font-size: 1.25rem;
      max-width: 100%;
    }
  `,
  button: css`
    background: linear-gradient(90deg, #6a11cb 0%, #2575fc 100%);
    border: none;
    border-radius: 8px;
    color: white;
    font-weight: 600;
    font-size: 1rem;
    padding: 0.75rem 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 4px 12px rgba(37, 117, 252, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(37, 117, 252, 0.4);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 8px rgba(37, 117, 252, 0.3);
    }
  `,
  secondaryButton: css`
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: #e0e0e0;
    font-weight: 500;
    font-size: 0.875rem;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.3);
    }
  `,
};

export const InvitationModal: React.FC = () => {
  const [, authInfo] = useAuthStatus();
  const {
    showAccountInvitationModal,
    currentPagePath,
    resetInvitation,
  } = useGlobalAppStore();

  // Don't show the modal if the user is already logged in
  useEffect(() => {
    if (showAccountInvitationModal && authInfo?.isAuthenticated) {
      resetInvitation();
    }
  }, [showAccountInvitationModal, authInfo, resetInvitation]);

  const handleClose = () => {
    resetInvitation();
  };

  const handleCreateAccount = () => {
    // Navigate to login page with next parameter set to return to current page
    const returnUrl = encodeURIComponent(
      currentPagePath || window.location.pathname
    );
    window.location.href = `/account/login?next=${returnUrl}`;
    resetInvitation();
  };

  return (
    <Modal
      title={null}
      open={showAccountInvitationModal && !authInfo?.isAuthenticated}
      onCancel={handleClose}
      footer={null}
      centered
      maskClosable={true}
      closable={true}
      className={css`
        .ant-modal-content {
          background-color: #1f1f1f;
          border-radius: 12px;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.5);
        }

        .ant-modal-body {
          padding: 0;
        }

        .ant-modal-close {
          color: #999;

          &:hover {
            color: #fff;
          }
        }
      `}
      width={380}
    >
      <div className={modalStyles.content}>
        <h3
          className={css`
            color: #fff;
            font-size: 1.4rem;
            margin-bottom: 0.75rem;
            font-weight: 600;
          `}
        >
          Unlock This Feature
        </h3>
        <p className={modalStyles.message}>
          Sign up to get <strong>free</strong> access.
          Explore interactive summaries and boost your learning. 🧠🚀
        </p>
        <button onClick={handleCreateAccount} className={modalStyles.button}>
          <FontAwesomeIcon icon={faUserPlus} />
          Create Free Account
        </button>
        <a
          href="https://info.sharpana.ai"
          target="_blank"
          rel="noopener noreferrer"
          className={css`
            background: transparent;
            border: 2px solid transparent;
            background-image: linear-gradient(#1f1f1f, #1f1f1f),
              linear-gradient(90deg, rgba(106, 17, 203, 0.5), rgba(37, 117, 252, 0.5));
            background-origin: border-box;
            background-clip: padding-box, border-box;
            border-radius: 8px;
            color: #f5f5f5;
            font-weight: 500;
            font-size: 0.9375rem;
            padding: 0.6rem 1.25rem;
            margin-top: 1rem;
            cursor: pointer;
            transition: all 0.3s ease-in-out;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            box-shadow: 0 0 0 rgba(37, 117, 252, 0);

            &:hover {
              background-image: linear-gradient(
                  rgba(106, 17, 203, 0.08),
                  rgba(37, 117, 252, 0.08)
                ),
                linear-gradient(90deg, rgba(106, 17, 203, 0.8), rgba(37, 117, 252, 0.8));
              box-shadow: 0 0 15px rgba(37, 117, 252, 0.3);
              transform: translateY(-1px);
              
              svg {
                transform: translateX(3px);
              }
            }

            svg {
              transition: transform 0.3s ease-in-out;
              font-size: 0.85rem;
            }
          `}
        >
          See What's Inside
          <FontAwesomeIcon icon={faArrowRight} />
        </a>
      </div>
    </Modal>
  );
};

export default InvitationModal;
